import React, { useState, useRef, useEffect } from 'react';
import PropTypes, { number } from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  NamedLink,
  FieldPassword,
  FieldNumberInput,
  InlineTextButton,
  Modal,
} from '../../components';
import * as validators from '../../util/validators';
import { ReactComponent as CloseIcon } from '../../assets/svg/close.svg';
import modalImg1 from '../../assets/norton-shake.png';
import modalImg2 from '../../assets/shutterstock_1158389167.png';
import modalBusiness from '../../assets/svg/modalBusiness.svg';
import modalWorker from '../../assets/svg/modalWorker.svg';
import css from './LoginForm.module.css';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { isEmpty } from 'lodash';
import { apiBaseUrl } from '../../util/api';
import axios from '../../util/axiosHelper';
import config from '../../config';

const textfield = 'textfield';
const errorTextField = 'errorTextField';
const LoginFormComponent = props => {
  const {
    onSubmit,
    loginError,
    loginStep: propsLoginStep,
    isInvalidOtpError,
    userNativeLang,
    onManageDisableScrolling,
    ...restProps
  } = props;
  const [loginStep, setLoginStep] = useState(propsLoginStep);
  const [open, setOpen] = useState(false);
  const [otpSendInProgress, setOtpSendInProgress] = useState(false);
  const [otpVerificationInProgress, setOtpVerificationInProgress] = useState(false);
  const [otpResend, setOtpResend] = useState(false);
  const formApi = useRef(null);
  const closeAuthModal = () => {
    setOpen(false);
  };
  const handleSubmit = async values => {
    const { errors } = formApi.current.getState();
    const {
      email,
      customEmailErrTextId,
      isOtpValidated = false,
      customOtpErrTextId,
      password,
      otp,
      token,
    } = values;

    const shouldReturn =
      !isEmpty(errors) ||
      !!customEmailErrTextId ||
      otpSendInProgress ||
      otpVerificationInProgress ||
      !!customOtpErrTextId;

    if (shouldReturn) return;

    if (isOtpValidated) {
      onSubmit({ email, password, token, otp });
    } else {
      if (loginStep === 0) {
        try {
          // console.log(111);
          const isAuthEnabled = await axios.post(
            `${apiBaseUrl()}/api/user/twoStepAuthenticationStatus`,
            {
              email,
              userNativeLang,
            },
            { withCredentials: true }
          );
          // console.log(555, isAuthEnabled);
          if (isAuthEnabled?.data === 'yes') {
            // console.log(6);
            generatePhoneOtp(email);
          } else {
            onSubmit({ email, password, token, otp });
          }
        } catch (e) {
          const errMessage = e?.response?.data || (
            <FormattedMessage id="LoginForm.FailedToSendOtp" />
          );

          formApi.current.change('otpSendErrorId', errMessage);
          // console.log(222, e);
        }
      } else {
        verifyPhoneOtp(email, otp, password);
      }
    }
  };

  const generatePhoneOtp = email => {
    const url = `${apiBaseUrl()}/api/user/generate/phoneotp`;
    formApi.current.change('otpSendErrorId', '');
    setOtpSendInProgress(true);
    return axios
      .post(url, { email })
      .then(resp => {
        setOtpSendInProgress(false);
        setLoginStep(1);
      })
      .catch(err => {
        setOtpSendInProgress(false);
        if (err.response.status === 404) {
          formApi.current.change('customEmailErrTextId', 'LoginForm.emailInvalid');
        } else if (err.response.status === 400) {
          formApi.current.change('customEmailErrTextId', 'LoginForm.emailRequired');
        } else {
          formApi.current.change('otpSendErrorId', 'LoginForm.otpSendFailed');
        }
      });
  };

  const verifyPhoneOtp = (email, otp, password) => {
    const url = `${apiBaseUrl()}/api/user/validate/phoneotp`;
    setOtpVerificationInProgress(true);
    return axios
      .post(url, { email, otp })
      .then(_ => {
        formApi.current.change('isOtpValidated', true);
        onSubmit({ email, password });
      })
      .catch(_ => {
        setOtpVerificationInProgress(false);
        formApi.current.change('customOtpErrTextId', 'LoginForm.otpInvalid');
      });
  };

  const handleOtpResend = e => {
    e.preventDefault();
    e.stopPropagation();
    setOtpResend(true);
    const {
      values: { email },
    } = formApi.current.getState();
    generatePhoneOtp(email).then(() => {
      setTimeout(() => {
        setOtpResend(false);
      }, 1300);
    });
  };

  useEffect(() => {
    if (!!loginError) {
      setOtpVerificationInProgress(false);
      setLoginStep(propsLoginStep);
      if (propsLoginStep === 0) {
        formApi.current.change('otp', '');
      }
      if (isInvalidOtpError) {
        formApi.current.change('customOtpErrTextId', 'LoginForm.otpInvalid');
      }
    }
  }, [loginError, propsLoginStep, isInvalidOtpError]);

  useEffect(() => {
    setLoginStep(propsLoginStep);
  }, [propsLoginStep]);

  return (
    <FinalForm
      {...restProps}
      onSubmit={handleSubmit}
      otpSendInProgress={otpSendInProgress}
      handleOtpResend={handleOtpResend}
      otpVerificationInProgress={otpVerificationInProgress}
      loginError={loginError}
      isInvalidOtpError={isInvalidOtpError}
      propsLoginStep={propsLoginStep}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          inProgress,
          intl,
          invalid,
          loginError,
          history,
          values,
          form,
          socialLoginButtonsMaybe,
          otpSendInProgress,
          handleOtpResend,
          isInvalidOtpError,
          propsLoginStep,
        } = fieldRenderProps;
        formApi.current = form;

        const {
          email,
          customEmailErrTextId = '',
          otpSendErrorId,
          customOtpErrTextId,
          otp,
        } = values;
        const emailTouched = !!email && !!customEmailErrTextId;
        const otpTouched = !!otp && !!customOtpErrTextId;
        const customEmailErrText =
          !!customEmailErrTextId && intl.formatMessage({ id: customEmailErrTextId });
        const customOtpErrText =
          !!customOtpErrTextId && intl.formatMessage({ id: customOtpErrTextId });

        const otpSendError = !!otpSendErrorId ? (
          <div className={css.error}>
            <FormattedMessage id={otpSendErrorId} />
          </div>
        ) : null;
        const sumitError =
          (loginStep === propsLoginStep && !isInvalidOtpError && loginError) || otpSendError;

        // email
        const emailLabel = intl.formatMessage({
          id: 'LoginForm.emailLabel',
        });
        const emailPlaceholder = intl.formatMessage({
          id: 'LoginForm.emailPlaceholder',
        });
        const emailRequiredMessage = intl.formatMessage({
          id: 'LoginForm.emailRequired',
        });
        const emailRequired = validators.required(emailRequiredMessage);
        const emailInvalidMessage = intl.formatMessage({
          id: 'LoginForm.emailInvalid',
        });
        const emailValid = validators.emailFormatValid(emailInvalidMessage);

        // password
        const passwordLabel = intl.formatMessage({
          id: 'LoginForm.passwordLabel',
        });
        const passwordPlaceholder = intl.formatMessage({
          id: 'LoginForm.passwordPlaceholder',
        });
        const passwordRequiredMessage = intl.formatMessage({
          id: 'LoginForm.passwordRequired',
        });
        const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);

        //otp
        const otpPlaceholder = intl.formatMessage({
          id: 'LoginForm.otpPlaceholder',
        });
        const otpRequiredMessage = intl.formatMessage({
          id: 'LoginForm.otpRequired',
        });
        // const otpInvalidMessage = intl.formatMessage({
        //   id: 'LoginForm.otpInvalid',
        // });

        const otpValidator = validators.composeValidators(validators.required(otpRequiredMessage));

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress =
          inProgress || (otpSendInProgress && loginStep === 0) || otpVerificationInProgress;
        const submitDisabled =
          invalid ||
          submitInProgress ||
          !!customEmailErrText ||
          (loginStep === 1 && otpSendInProgress) ||
          customOtpErrText;
        // console.log(
        //   123,
        //   invalid,
        //   submitInProgress,
        //   !!customEmailErrText,
        //   otpSendInProgress,
        //   loginStep === 1 && otpSendInProgress,
        //   customOtpErrText
        // );
        const passwordRecoveryLink = (
          <NamedLink name="PasswordRecoveryPage" className={css.recoveryLink}>
            <FormattedMessage id="LoginForm.forgotPassword" />
          </NamedLink>
        );

        const signupLink = (
          <span className={css.signupLink} onClick={() => setOpen(!open)}>
            <FormattedMessage id="LoginForm.signupInfo" />
          </span>
        );

        const resendLink = (
          <InlineTextButton type="button" className={css.resendBtn} onClick={handleOtpResend}>
            <FormattedMessage id="LoginForm.resend" />
          </InlineTextButton>
        );

        const handleOnClose = e => {
          e.preventDefault();
          const path = pathByRouteName('LandingPage', routeConfiguration());
          // history.replace(createResourceLocatorString('LandingPage', routeConfiguration()));
          if (config.dev) {
            if (process.env.REACT_APP_CANONICAL_ROOT_URL?.includes('localhost:'))
              history.push(path);
            else window.location.href = 'https://djob.co/';
          } else if (typeof window !== 'undefined') {
            if (process.env.REACT_APP_CANONICAL_ROOT_URL?.includes('localhost:'))
              window.location = path;
            else window.location.href = 'https://djob.co/';
          }
          if (config.dev) {
            if (process.env.REACT_APP_CANONICAL_ROOT_URL?.includes('localhost:'))
              history.push(path);
            else window.location.href = 'https://djob.co/';
          } else if (typeof window !== 'undefined') {
            if (process.env.REACT_APP_CANONICAL_ROOT_URL?.includes('localhost:'))
              window.location = path;
            else window.location.href = 'https://djob.co/';
          }
        };

        const handleEmailChange = value => {
          form.batch(() => {
            form.change('email', value);
            form.change('customEmailErrTextId', '');
            form.change('otpSendErrorId', null);
            form.change('isOtpValidated', false);
          });
        };

        const handleOtpChange = value => {
          form.batch(() => {
            form.change('customOtpErrTextId', '');
            form.change('otp', value);
          });
        };

        return (
          <>
            <Form className={classes} onSubmit={handleSubmit}>
              <button type="button" onClick={handleOnClose} className={css.closeBtn}>
                <CloseIcon />
              </button>
              <div className={css.headingWrapper}>
                <div>
                  <FormattedMessage
                    id={loginStep === 0 ? 'LoginForm.heading' : 'LoginForm.twoFactorHeading'}
                  />
                </div>
                <p>
                  <FormattedMessage
                    id={loginStep === 0 ? 'LoginForm.subHeading' : 'LoginForm.twoFactorSubheading'}
                    values={{ signupLink }}
                  />
                </p>
              </div>
              {loginStep == 0 ? (
                <div className={css.LoginFieldGrp}>
                  <FieldTextInput
                    className={textfield}
                    errorClassName={errorTextField}
                    type="email"
                    id={formId ? `${formId}.email` : 'email'}
                    name="email"
                    autoComplete="email"
                    label={emailLabel}
                    placeholder={emailPlaceholder}
                    validate={validators.composeValidators(emailRequired, emailValid)}
                    customErrorText={emailTouched ? customEmailErrText : null}
                    onChange={handleEmailChange}
                  />
                  <FieldPassword
                    id={formId ? `${formId}.password` : 'password'}
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    label={passwordLabel}
                    placeholder={passwordPlaceholder}
                    validate={passwordRequired}
                    className={textfield}
                    errorClassName={errorTextField}
                    pwdStrgnthCheckReq={false}
                  />
                  {/* <FieldTextInput
                // className={css.password}
                className="textfield"
                type="password"
                id={formId ? `${formId}.password` : 'password'}
                name="password"
                autoComplete="current-password"
                label={passwordLabel}
                placeholder={passwordPlaceholder}
                validate={passwordRequired}
              /> */}
                </div>
              ) : (
                <div>
                  <FieldNumberInput
                    id="otp"
                    name="otp"
                    placeholder={otpPlaceholder}
                    validate={otpValidator}
                    className={textfield}
                    errorClassName={errorTextField}
                    onChange={handleOtpChange}
                    customErrorText={otpTouched ? customOtpErrText : null}
                  />
                  <div className={css.resendOtpDiv}>
                    {otpResend ? (
                      <FormattedMessage id="LoginForm.resendingOtpInfo" />
                    ) : (
                      <FormattedMessage id="LoginForm.resendInfo" values={{ resendLink }} />
                    )}
                  </div>
                </div>
              )}
              <div className={css.bottomWrapper}>
                {!!sumitError && <div className={css.errorWrapper}>{sumitError}</div>}
                <PrimaryButton
                  type="submit"
                  inProgress={submitInProgress}
                  disabled={submitDisabled}
                  className={css.submitBtn}
                >
                  <FormattedMessage id={loginStep === 0 ? 'LoginForm.logIn' : 'LoginForm.verify'} />
                </PrimaryButton>
                {loginStep === 0 && (
                  <span className={css.recoveryLinkInfo}>
                    <FormattedMessage
                      id="LoginForm.forgotPasswordInfo"
                      values={{ passwordRecoveryLink }}
                    />
                  </span>
                )}
              </div>
              {loginStep === 0 && <div className={css.LoginSoclBtn}>{socialLoginButtonsMaybe}</div>}
            </Form>
            <Modal
              id="ContactDetails.showAuthModal"
              isOpen={open}
              onClose={closeAuthModal}
              scrollLayerClassName={css.modalScrollLayer}
              containerClassName={css.modalContainer1}
              contentClassName={css.modalContent}
              onManageDisableScrolling={onManageDisableScrolling}
              usePortal
            >
              <div className={css.modalDiv}>
                <p className={css.modalHeading}>
                  <FormattedMessage id="BusinessWeServe.modalHeading" />
                </p>
                <p className={css.accLogin_text}>
                  <FormattedMessage id="LandingPage.alreadyAccountMessage" />
                  {/* <span className={css.accLogin_text1}>
              {' '} */}
                  <span
                    className={css.login}
                    onClick={e => {
                      e.stopPropagation();
                      // history.push(
                      //   createResourceLocatorString('LoginPage', routeConfiguration(), {}, {})
                      // );
                      setOpen(!open);
                    }}
                  >
                    {' '}
                    <FormattedMessage id="LandingPage.logInMessage" />{' '}
                  </span>{' '}
                  {/* </span>{' '} */}
                </p>
                <div className={css.modalParent}>
                  <div className={css.modalChild}>
                    <img src={modalImg1} alt="" />
                    <div className={css.modalDetail}>
                      <div className={css.modalDetailiconImgDiv}>
                        <img src={modalBusiness} alt="" />
                      </div>
                      <p className={css.modaltext}>
                        <FormattedMessage id="BusinessWeServe.modalBusiness" />{' '}
                      </p>
                      <div className={css.herobuttons}>
                        <span
                          className={css.staff}
                          onClick={e => {
                            e.stopPropagation();
                            history.push(
                              createResourceLocatorString(
                                'SignupAsCompanyPage',
                                routeConfiguration(),
                                {},
                                {}
                              )
                            );
                          }}
                        >
                          <FormattedMessage id="LandingPage.buttonStaffMessage" />
                        </span>
                      </div>
                      {/* <div className='learnMore'>
                  <p>Learn More</p>
                </div> */}
                    </div>
                    <div className={css.learnMore}>
                      <p
                        onClick={e => {
                          e.stopPropagation();
                          history.push(
                            createResourceLocatorString(
                              'LookingForStaff',
                              routeConfiguration(),
                              {},
                              {}
                            )
                          );
                        }}
                      >
                        <FormattedMessage id="BusinessWeServe.modalLearnMore" />{' '}
                      </p>
                    </div>
                  </div>
                  <div className={css.modalChild}>
                    <img src={modalImg2} alt="" />
                    <div className={css.modalDetail}>
                      <div className={css.modalDetailiconImgDiv}>
                        <img src={modalWorker} alt="" />
                      </div>
                      <p className={css.modaltext}>
                        <FormattedMessage id="BusinessWeServe.modalWorker" />{' '}
                      </p>
                      <div className={css.herobuttons}>
                        <span
                          className={css.work}
                          onClick={e => {
                            e.stopPropagation();
                            history.push(
                              createResourceLocatorString(
                                'SignupPage',
                                routeConfiguration(),
                                {},
                                {}
                              )
                            );
                          }}
                        >
                          {' '}
                          <FormattedMessage id="LandingPage.buttonWorkMessage" />
                        </span>
                      </div>
                    </div>
                    <div className={css.learnMore}>
                      <p
                        onClick={e => {
                          e.stopPropagation();
                          history.push(
                            createResourceLocatorString(
                              'LookingForWork',
                              routeConfiguration(),
                              {},
                              {}
                            )
                          );
                        }}
                      >
                        <FormattedMessage id="BusinessWeServe.modalLearnMore" />{' '}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Modal>
          </>
        );
      }}
    />
  );
};

LoginFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  form: null,
  inProgress: false,
  loginStep: 0,
};

const { string, bool } = PropTypes;

LoginFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  form: string,
  inProgress: bool,
  intl: intlShape.isRequired,
  loginStep: number.isRequired,
};

const LoginForm = compose(injectIntl)(LoginFormComponent);
LoginForm.displayName = 'LoginForm';

export default LoginForm;
